/**
 * One adapter between the governed form view and the finite layout document.
 *
 * This belongs with the experience grammar rather than in either host: console
 * records and portal forms must derive the same stable section/field identities,
 * reconcile the same way and preserve the same field semantics. Hosts choose
 * only the admitted surface and the door through which a document is saved.
 */
import {
  LAYOUT_SCHEMA_VERSION,
  LAYOUT_SCHEMA_VERSION_V2,
  normalizeLayoutDocument,
  reconcileLayoutDocument,
  validateLayoutDocument,
  type LayoutCatalog,
  type LayoutConflict,
  type LayoutDocument,
  type LayoutDocumentV2,
  type PersistedLayoutDocument,
  type LayoutSurface,
} from './layout.js'
import type { FormOrder, FormSection, FormView } from './views.js'

const slug = (value: string): string => {
  const clean = value.toLocaleLowerCase().normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
  return clean || 'section'
}

/** The metamodel form as the finite layout grammar. Related panels keep their
 * own governed relationship document; this adapter owns fields/sections only. */
export function formLayout(view: FormView, surface: Extract<LayoutSurface, 'console_form' | 'portal_form'> = 'console_form'): {
  document: LayoutDocument
  catalog: LayoutCatalog
  labels: Record<string, string>
} {
  const seen = new Map<string, number>()
  const sections = view.sections.filter((section) => section.fields.length > 0).map((section) => {
    const stem = `section.${slug(section.title)}`
    const occurrence = (seen.get(stem) ?? 0) + 1
    seen.set(stem, occurrence)
    const id = occurrence === 1 ? stem : `${stem}.${occurrence}`
    return {
      id,
      label: section.title,
      ...(section.description ? { description: section.description } : {}),
      columns: 2 as const,
      items: section.fields.map((field) => ({
        id: `field.${field.field}`,
        kind: 'field' as const,
        key: field.field,
        span: { desktop: 1 as const, tablet: 1 as const, phone: 1 as const },
      })),
    }
  })
  const labels = Object.fromEntries(view.sections.flatMap((section) => section.fields)
    .map((field) => [field.field, field.label]))
  const catalogueItems = sections.flatMap((section) => section.items.map((item) => {
    const column = view.sections.flatMap((candidate) => candidate.fields)
      .find((field) => field.field === item.key)
    return { id: item.id, kind: item.kind, key: item.key, required: Boolean(column?.required), sectionId: section.id }
  }))
  return {
    document: {
      schemaVersion: LAYOUT_SCHEMA_VERSION,
      id: `form.${view.entity}`,
      revision: 1,
      surface,
      entity: view.entity,
      sections,
      unplaced: [],
      dependencies: [
        { kind: 'entity', key: view.entity },
        ...catalogueItems.map((item) => ({ kind: 'field' as const, key: item.key })),
      ],
    },
    catalog: {
      items: catalogueItems,
      dependencies: [
        { kind: 'entity', key: view.entity },
        ...catalogueItems.map((item) => ({ kind: 'field' as const, key: item.key })),
      ],
    },
    labels,
  }
}

export interface ResolvedFormLayout {
  /** Always v2: one in-memory grammar, whichever grammar was stored. */
  document: LayoutDocumentV2
  conflicts: LayoutConflict[]
  invalid: string[]
}

/**
 * Read the finite grammar out of an experience row.
 *
 * This used to refuse a v2 document outright, with the reason recorded in
 * place: "until the shared v2 renderer lands, an old host must fall back to its
 * governed v1 base instead of treating responsive columns as a scalar". The
 * shared renderer landed with the rest of #2009 — `FormLayoutSections`, adopted
 * by both hosts — so the refusal now does the opposite of what it was for. An
 * author who saved responsive geometry would have watched the form come back
 * with the generated layout and no error anywhere.
 *
 * The version is still not trusted blindly: an unknown `schemaVersion` is
 * refused by the validator below, exactly as it was.
 */
export function layoutFromExperience(value: unknown): PersistedLayoutDocument | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const record = value as Record<string, unknown>
  const candidate = record.layout ?? value
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return null
  const version = (candidate as Record<string, unknown>).schemaVersion
  if (version !== LAYOUT_SCHEMA_VERSION && version !== LAYOUT_SCHEMA_VERSION_V2) return null
  return validateLayoutDocument(candidate).length === 0 ? candidate as PersistedLayoutDocument : null
}

/** Reduce the visual editor to the already-proven personal preference rung. */
export function formOrderFromLayout(layout: PersistedLayoutDocument): FormOrder {
  return {
    sections: layout.sections.map((section) => section.label),
    fields: Object.fromEntries(layout.sections.map((section) => [
      section.label,
      section.items.filter((item) => item.kind === 'field').map((item) => item.key),
    ])),
  }
}

/** Invalid persisted input fails toward the governed definition, never blank. */
export function resolveFormLayout(
  saved: unknown,
  base: PersistedLayoutDocument,
  catalog: LayoutCatalog,
): ResolvedFormLayout {
  /* BOTH sides normalized before they meet. Reconciliation copies items out of
     the base into the authored document verbatim, so mixing grammars here would
     post v1-shaped items into a v2 document — the one way this seam could
     manufacture a document neither validator would accept. */
  const baseline = normalizeLayoutDocument(base)
  if (saved == null) return { document: baseline, conflicts: [], invalid: [] }
  const invalid = validateLayoutDocument(saved)
  if (invalid.length) return { document: baseline, conflicts: [], invalid }
  const reconciled = reconcileLayoutDocument(normalizeLayoutDocument(saved), baseline, catalog)
  return { document: reconciled.document, conflicts: reconciled.conflicts, invalid: [] }
}

/**
 * Apply presentation only. Field meaning and all related declarations survive.
 *
 * ── the geometry a host receives is FINISHED (#2009) ──────────────────────
 *
 * Two things happen here that used to happen in each host, differently:
 *
 *  1. NORMALIZATION. Either persisted grammar is read into one in-memory v2
 *     document by `normalizeLayoutDocument`, so a reader never branches on
 *     schema version and the stored row is never rewritten. Before this, the
 *     resolved view carried the v1 scalar and each host invented the other two
 *     profiles — console through CSS media queries, portal by ignoring them and
 *     always applying the DESKTOP span. A configured tablet span therefore
 *     rendered differently in the two hosts, which is #2009's defect 3.
 *
 * NO CLAMPING HAPPENS HERE, and that is a measured decision rather than an
 * omission. The first draft of this function clamped each span to its profile's
 * track "in case" a section had been narrowed under a wide item. Writing the
 * test for it proved the case cannot occur: `normalizeLayoutDocument` VALIDATES
 * before it adapts, and the validator refuses
 * `span.<profile> > section.columns.<profile>` — so such a document cannot be
 * read at all, let alone reach a renderer. The clamp was unreachable code with
 * a persuasive comment attached, which is worse than no clamp.
 *
 * The one genuinely reachable version of that worry is a section built BY HAND
 * that never passed through this adapter — a fixture, a unit test, a future
 * caller. That is handled where it can actually happen, by `min(span, cols)` in
 * the shared renderer's stylesheet.
 */
export function applyFormLayout(view: FormView, layout: PersistedLayoutDocument): FormView {
  const document = normalizeLayoutDocument(layout)
  const columns = new Map(view.sections.flatMap((section) => section.fields)
    .map((field) => [field.field, field]))
  const fields: FormSection[] = document.sections.flatMap((section) => {
    const spans = new Map(section.items.map((item) => [item.key, item.span]))
    const placed = section.items
      .filter((item) => item.kind === 'field' && !item.hidden)
      .flatMap((item) => columns.get(item.key)
        ? [{ ...columns.get(item.key)!, layoutSpan: spans.get(item.key) }]
        : [])
    return placed.length ? [{
      title: section.label,
      description: section.description,
      fields: placed,
      layoutId: section.id,
      layoutColumns: section.columns,
    }] : []
  })
  const related = view.sections.filter((section) => section.related)
  return { ...view, sections: [...fields, ...related] }
}
